import { APPLE_SCOPE, SDK_UNAVAILABLE } from './constants';
import { loadSdk } from './loadSdk';
import { providerError } from './providerError';
import type { AppleSignInResponse } from './types';

/**
 * Runs the Sign in with Apple popup and resolves with the Apple response.
 *
 * `usePopup` keeps the top document alive. A full-page redirect moves the user
 * off the landing page. It also sends the result to a server-side Return URL,
 * which the frontend never reads.
 *
 * Apple needs `redirectURI` in popup mode too, so the Service ID must hold it
 * as a Return URL. Apple copies the nonce into the `nonce` claim of the ID
 * token. The backend then compares it with the stored value.
 */
const signInWithApple = async ({
  clientId,
  redirectUri,
  nonce,
  sdkSrc,
}: {
  clientId: string;
  redirectUri: string;
  nonce: string;
  /** The URL the site settings give. The caller checks that one exists. */
  sdkSrc: string;
}): Promise<AppleSignInResponse> => {
  await loadSdk(sdkSrc);

  if (!window.AppleID) {
    throw providerError(SDK_UNAVAILABLE, 'Sign in with Apple is unavailable');
  }

  window.AppleID.auth.init({
    clientId,
    scope: APPLE_SCOPE,
    redirectURI: redirectUri,
    nonce,
    usePopup: true,
  });

  return window.AppleID.auth.signIn();
};

export { signInWithApple };
